/**
 * Running-balance register (peer pattern: AppFolio / DoorLoop / Baselane).
 *
 * Turns the per-month ledger + raw payments into a single chronological list of
 * entries with a Balance that carries down: charges (rent, CAM, GST, electricity)
 * INCREASE the balance; the tenant's TDS deduction and every payment REDUCE it.
 * TDS is always its own line (India, Sec 194-I). Money stays integer paise — the
 * UI formats. Pure + deterministic; both owner and tenant surfaces build from it.
 */
import type { LedgerRow, RegisterPayment } from './api';
import { fmtPeriod } from './format';

export type RegisterKind = 'opening' | 'charge' | 'tds' | 'payment';

export interface RegisterEntry {
  key: string;
  date: number | null; // epoch ms (charge = due date, payment = paid-on)
  period: string; // "YYYY-MM" ('' for the opening row)
  description: string;
  sub?: string;
  kind: RegisterKind;
  charge: number; // paise (0 when none)
  credit: number; // paise reducing the balance (0 when none)
  balance: number; // running balance after this entry, paise
}

const methodLabel: Record<string, string> = {
  neft: 'NEFT',
  upi: 'UPI',
  cheque: 'Cheque',
  cash: 'Cash',
  other: 'Payment',
};

export interface BuildRegisterInput {
  periods: LedgerRow[];
  payments: RegisterPayment[];
  openingBalance: number;
  tdsRatePct: number;
}

/**
 * Build the register, oldest-first. Order within a month: charge lines (rent, then
 * CAM / GST / electricity when present), then the TDS credit, then each payment in
 * paid-on order — so the balance reads exactly as a statement would.
 */
export function buildRegister({
  periods,
  payments,
  openingBalance,
  tdsRatePct,
}: BuildRegisterInput): RegisterEntry[] {
  const rows: RegisterEntry[] = [];
  let bal = openingBalance;

  if (openingBalance !== 0) {
    rows.push({
      key: 'opening',
      date: null,
      period: '',
      description: 'Opening balance',
      sub: 'Brought forward',
      kind: 'opening',
      charge: 0,
      credit: 0,
      balance: bal,
    });
  }

  const payByPeriod = new Map<string, RegisterPayment[]>();
  for (const p of payments) {
    const list = payByPeriod.get(p.period) ?? [];
    list.push(p);
    payByPeriod.set(p.period, list);
  }

  const charge = (
    period: string,
    date: number | null,
    description: string,
    amount: number,
    sub?: string,
    tag = 'chg',
  ) => {
    bal += amount;
    rows.push({ key: `${period}-${tag}`, date, period, description, sub, kind: 'charge', charge: amount, credit: 0, balance: bal });
  };

  for (const r of periods) {
    const month = fmtPeriod(r.period);
    // Rent (base + any escalation) as one charge line.
    charge(
      r.period,
      r.dueDate ?? null,
      `Rent · ${month}`,
      r.rent,
      r.escalationAmount > 0 ? 'Includes escalation' : undefined,
      'rent',
    );
    if (r.camAmount > 0) charge(r.period, r.dueDate ?? null, `CAM · ${month}`, r.camAmount, 'Common-area maintenance', 'cam');
    if (r.gstAmount > 0) charge(r.period, r.dueDate ?? null, `GST · ${month}`, r.gstAmount, 'Added to invoice', 'gst');
    if (r.electricityAmount > 0)
      charge(r.period, r.dueDate ?? null, `Electricity · ${month}`, r.electricityAmount, 'Metered', 'elec');

    if (r.tdsAmount > 0) {
      bal -= r.tdsAmount;
      rows.push({
        key: `${r.period}-tds`,
        date: r.dueDate ?? null,
        period: r.period,
        description: `TDS deducted (${tdsRatePct}%)`,
        sub: 'Withheld by tenant · Sec 194-I',
        kind: 'tds',
        charge: 0,
        credit: r.tdsAmount,
        balance: bal,
      });
    }

    const pays = (payByPeriod.get(r.period) ?? []).slice().sort((a, b) => (a.paidOn ?? 0) - (b.paidOn ?? 0));
    for (const p of pays) {
      bal -= p.amount;
      const m = methodLabel[p.method ?? 'other'] ?? 'Payment';
      rows.push({
        key: `pay-${p.id}`,
        date: p.paidOn ?? null,
        period: r.period,
        description: 'Payment received',
        sub: p.reference ? `${m} · ${p.reference}` : m,
        kind: 'payment',
        charge: 0,
        credit: p.amount,
        balance: bal,
      });
    }
  }

  return rows;
}
